import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Star, ShieldCheck } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const HERO_IMG =
  "https://images.pexels.com/photos/29012619/pexels-photo-29012619.jpeg?auto=compress&cs=tinysrgb&w=1920";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.95, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-[92vh] items-end overflow-hidden"
    >
      <motion.div style={{ scale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Pristine bright living room cleaned by Hyek"
          className="h-full w-full object-cover"
          fetchpriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-900/25 to-slate-900/10" />

      <motion.div style={{ y }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-16 pt-44 lg:px-12 lg:pb-24">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          className="mb-6 text-xs font-bold uppercase tracking-[0.25em] text-teal-200"
        >
          Hyek Cleaning Services Ltd
        </motion.p>

        <h1 className="font-serif text-4xl font-medium leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.25}>Sparkling spaces,</MaskedLine>
          <MaskedLine delay={0.42}>
            <span className="italic text-teal-200">healthier living.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.68, duration: 0.7 }}
          className="mt-6 max-w-xl text-sm leading-relaxed text-slate-100/90 sm:text-base"
        >
          Premium home and office cleaning, end of tenancy deep cleans and carpet care — delivered
          by vetted, fully insured professionals who treat your space like their own.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.82, duration: 0.7 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-quote-button"
            onClick={() => scrollToSection("#contact")}
            className="group flex items-center gap-2 rounded-full bg-teal-500 px-8 py-4 text-sm font-bold text-slate-950 shadow-lg shadow-teal-950/30 transition-transform duration-300 hover:scale-105 hover:bg-teal-400"
          >
            Get a Free Quote
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-services-button"
            onClick={() => scrollToSection("#services")}
            className="rounded-full border border-white/40 bg-white/10 px-8 py-4 text-sm font-bold text-white backdrop-blur-sm transition-colors duration-300 hover:bg-white/20"
          >
            Explore Services
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs font-semibold text-slate-200"
        >
          <span className="flex items-center gap-2">
            <span className="flex text-teal-300">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-3.5 w-3.5 fill-current" />
              ))}
            </span>
            Rated 5.0 by local homes &amp; offices
          </span>
          <span className="hidden h-4 w-px bg-white/30 sm:block" />
          <span className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-teal-300" />
            Fully insured &amp; vetted team
          </span>
        </motion.div>
      </motion.div>
    </section>
  );
};
